import { AccountabilityChartNode } from 'types/api/Tenant/AccountabilityTypes';

export interface AccountabilityLevelFilterOption {
  id: string;
  label: string;
  ancestorIds: string[];
}

export interface AccountabilityLevelFilter {
  key: string;
  label: string;
  depth: number;
  options: AccountabilityLevelFilterOption[];
}

interface AccountabilityLevelBucket extends AccountabilityLevelFilter {
  hasExplicitLabel: boolean;
  optionIds: Set<string>;
}

function getNodeId(node: AccountabilityChartNode, path: number[]) {
  return node.ktreeMemberId?.trim() || `accountability-member-${path.join('-') || 'root'}`;
}

function getLevelLabel(node: AccountabilityChartNode, depth: number) {
  const explicitLabel = node.levelName?.trim();

  if (explicitLabel) return explicitLabel;
  if (node.level !== null && node.level !== undefined) return `Level ${node.level}`;
  return `Level ${depth}`;
}

/**
 * Derives one dropdown definition for every hierarchy depth below the chart root.
 * Member options retain their higher-level member IDs so they can be cascaded.
 */
export function deriveAccountabilityLevelFilters(root?: AccountabilityChartNode | null): AccountabilityLevelFilter[] {
  if (!root) return [];

  const levelsByDepth = new Map<number, AccountabilityLevelBucket>();

  const visit = (node: AccountabilityChartNode, depth: number, path: number[], ancestorIds: string[]) => {
    const id = getNodeId(node, path);
    const explicitLabel = node.levelName?.trim();
    let level = levelsByDepth.get(depth);

    if (!level) {
      level = {
        key: `accountability-level-${depth}`,
        label: getLevelLabel(node, depth),
        depth,
        options: [],
        hasExplicitLabel: Boolean(explicitLabel),
        optionIds: new Set(),
      };
      levelsByDepth.set(depth, level);
    } else if (!level.hasExplicitLabel && explicitLabel) {
      level.label = explicitLabel;
      level.hasExplicitLabel = true;
    }

    if (!level.optionIds.has(id)) {
      level.options.push({
        id,
        label: node.member?.trim() || 'Unnamed member',
        ancestorIds: [...ancestorIds],
      });
      level.optionIds.add(id);
    }

    (node.children ?? []).forEach((child, childIndex) => {
      visit(child, depth + 1, [...path, childIndex], [...ancestorIds, id]);
    });
  };

  (root.children ?? []).forEach((child, childIndex) => {
    visit(child, 1, [childIndex], []);
  });

  return [...levelsByDepth.values()]
    .sort((first, second) => first.depth - second.depth)
    .map(({ hasExplicitLabel, optionIds, ...level }) => level);
}

/** Returns only options descending from every currently selected higher-level member. */
export function restrictAccountabilityLevelOptions(
  level: AccountabilityLevelFilter,
  selectedHigherLevelIds: readonly (string | null | undefined)[],
): AccountabilityLevelFilterOption[] {
  const selectedIds = selectedHigherLevelIds.filter((id): id is string => Boolean(id));

  if (selectedIds.length === 0) return level.options;
  return level.options.filter((option) => selectedIds.every((id) => option.ancestorIds.includes(id)));
}

/**
 * Keeps the path from the root to the deepest selected member and leaves that
 * member's complete descendant subtree intact.
 */
export function filterAccountabilityTreeToSelection(
  root: AccountabilityChartNode,
  selectedNodeIds: readonly (string | null | undefined)[],
): AccountabilityChartNode {
  const selectedIds = new Set(selectedNodeIds.filter((id): id is string => Boolean(id)));

  if (selectedIds.size === 0) return root;

  const findDeepestSelection = (node: AccountabilityChartNode, path: number[]): number[] | null => {
    let deepestPath = selectedIds.has(getNodeId(node, path)) ? path : null;

    (node.children ?? []).forEach((child, childIndex) => {
      const childPath = findDeepestSelection(child, [...path, childIndex]);
      if (childPath && (!deepestPath || childPath.length > deepestPath.length)) deepestPath = childPath;
    });

    return deepestPath;
  };

  const selectedPath = findDeepestSelection(root, []);

  if (!selectedPath || selectedPath.length === 0) return root;

  const retainSelectedPath = (node: AccountabilityChartNode, remainingPath: number[]): AccountabilityChartNode => {
    if (remainingPath.length === 0) return node;

    const [childIndex, ...rest] = remainingPath;
    const selectedChild = (node.children ?? [])[childIndex];

    if (!selectedChild) return node;

    return {
      ...node,
      children: [retainSelectedPath(selectedChild, rest)],
    };
  };

  return retainSelectedPath(root, selectedPath);
}
